import { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firestore";
import { useAuth } from "../context/AuthContext";
import "./BmiTracker.scss";

// ── Constants ─────────────────────────────────────────────────────────────────

const RK_EMAIL = "rajeshmaydev@gmail.com";
const INDU_EMAIL = "indira02032001@gmail.com";

const USER_META = {
  [RK_EMAIL]: {
    name: "RK",
    initial: "R",
    lineColor: "var(--clr-p, #f4a5ae)",
    chipBg: "color-mix(in srgb, var(--clr-p, #f4a5ae) 20%, transparent)",
    borderColor: "color-mix(in srgb, var(--clr-p, #f4a5ae) 33%, transparent)",
  },
  [INDU_EMAIL]: {
    name: "Indu",
    initial: "I",
    lineColor: "#b9a5f4",
    chipBg: "#b9a5f433",
    borderColor: "#b9a5f455",
  },
};

const BMI_ZONES = [
  {
    label: "Underweight",
    max: 18.5,
    chipBg: "#dce8f5",
    chipText: "#4a6fa5",
    bandFill: "#dce8f5",
  },
  {
    label: "Normal",
    max: 25,
    chipBg: "#d4edda",
    chipText: "#2d6a4f",
    bandFill: "#d4edda",
  },
  {
    label: "Overweight",
    max: 30,
    chipBg: "#fff3cd",
    chipText: "#856404",
    bandFill: "#fff3cd",
  },
  {
    label: "Obese",
    max: Infinity,
    chipBg: "#f8d7da",
    chipText: "#842029",
    bandFill: "#f8d7da",
  },
];

// Body fat % zones — differ by sex (Gallagher et al.)
const BF_ZONES = {
  M: [
    { label: "Essential", max: 6, chipBg: "#e3f0ff", chipText: "#1a56db" },
    { label: "Athletic", max: 14, chipBg: "#d4edda", chipText: "#155724" },
    { label: "Fitness", max: 18, chipBg: "#d4edda", chipText: "#2d6a4f" },
    { label: "Average", max: 25, chipBg: "#fff3cd", chipText: "#856404" },
    { label: "Obese", max: Infinity, chipBg: "#f8d7da", chipText: "#842029" },
  ],
  F: [
    { label: "Essential", max: 14, chipBg: "#e3f0ff", chipText: "#1a56db" },
    { label: "Athletic", max: 21, chipBg: "#d4edda", chipText: "#155724" },
    { label: "Fitness", max: 25, chipBg: "#d4edda", chipText: "#2d6a4f" },
    { label: "Average", max: 32, chipBg: "#fff3cd", chipText: "#856404" },
    { label: "Obese", max: Infinity, chipBg: "#f8d7da", chipText: "#842029" },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const calcBmi = (w, h) => Math.round((w / Math.pow(h / 100, 2)) * 10) / 10;
const getBmiZone = (bmi) => BMI_ZONES.find((z) => bmi < z.max);
const getBfZone = (bf, sex) =>
  (BF_ZONES[sex] ?? BF_ZONES.M).find((z) => bf < z.max);
const todayStr = () => new Date().toISOString().slice(0, 10);

// Age at today
const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// Age at a specific log date (for accurate historical BF% calc)
const calcAgeAt = (dob, logDate) => {
  if (!dob || !logDate) return null;
  const birth = new Date(dob + "T00:00:00");
  const at = new Date(logDate + "T00:00:00");
  let age = at.getFullYear() - birth.getFullYear();
  const m = at.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// Deurenberg formula: BF% = (1.20 × BMI) + (0.23 × Age) − (10.8 × Sex) − 5.4
// Sex: 1 = male, 0 = female
const calcBodyFat = (bmi, dob, sex, logDate) => {
  if (!bmi || !dob || !sex) return null;
  const age = calcAgeAt(dob, logDate);
  if (age === null || age < 2) return null;
  const sexVal = sex === "M" ? 1 : 0;
  const bf = 1.2 * bmi + 0.23 * age - 10.8 * sexVal - 5.4;
  return Math.max(0, Math.round(bf * 10) / 10);
};

const fmtDate = (dateStr) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const fmtDateShort = (dateStr) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

function calcStreak(logs, email) {
  const weeks = [
    ...new Set(
      logs
        .filter((l) => l.loggedBy === email)
        .map((l) =>
          Math.floor(new Date(l.date + "T00:00:00").getTime() / (7 * 86400000)),
        ),
    ),
  ].sort((a, b) => b - a);

  if (!weeks.length) return 0;
  const currentWeek = Math.floor(Date.now() / (7 * 86400000));
  if (weeks[0] < currentWeek - 1) return 0;

  let streak = 1;
  for (let i = 0; i < weeks.length - 1; i++) {
    if (weeks[i] - weeks[i + 1] === 1) streak++;
    else break;
  }
  return streak;
}

// ── BmiTracker (root) ─────────────────────────────────────────────────────────

export default function BmiTracker() {
  const { user } = useAuth();

  const [profiles, setProfiles] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingProfiles, setLPro] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showAddWeight, setShowAddWeight] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [setupForm, setSetupForm] = useState({
    height: "",
    dob: "",
    sex: "",
    goalWeight: "",
  });
  const [weightForm, setWeightForm] = useState({
    weight: "",
    date: todayStr(),
  });

  // Profiles listener
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "health", "profiles"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setProfiles(data);
        setLPro(false);
        if (!data[user.email]?.height) setShowSetup(true);
      },
      () => setLPro(false),
    );
    return unsub;
  }, [user.email]);

  // Logs listener
  useEffect(() => {
    const q = query(collection(db, "healthLogs"), orderBy("date", "asc"));
    return onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────

  const derived = useMemo(() => {
    const rkH = profiles?.[RK_EMAIL]?.height ?? null;
    const induH = profiles?.[INDU_EMAIL]?.height ?? null;
    const rkDob = profiles?.[RK_EMAIL]?.dob ?? null;
    const induDob = profiles?.[INDU_EMAIL]?.dob ?? null;
    const rkSex = profiles?.[RK_EMAIL]?.sex ?? null;
    const induSex = profiles?.[INDU_EMAIL]?.sex ?? null;

    const rkLogs = logs
      .filter((l) => l.loggedBy === RK_EMAIL)
      .sort((a, b) => a.date.localeCompare(b.date));
    const induLogs = logs
      .filter((l) => l.loggedBy === INDU_EMAIL)
      .sort((a, b) => a.date.localeCompare(b.date));

    const bmi = (w, h) => (h ? calcBmi(w, h) : null);

    const rkLatest = rkLogs.at(-1);
    const rkPrev = rkLogs.at(-2);
    const induLatest = induLogs.at(-1);
    const induPrev = induLogs.at(-2);

    const rkBmi = rkLatest ? bmi(rkLatest.weight, rkH) : null;
    const rkPrevBmi = rkPrev ? bmi(rkPrev.weight, rkH) : null;
    const induBmi = induLatest ? bmi(induLatest.weight, induH) : null;
    const induPrevBmi = induPrev ? bmi(induPrev.weight, induH) : null;

    const rkGoalW = profiles?.[RK_EMAIL]?.goalWeight ?? null;
    const induGoalW = profiles?.[INDU_EMAIL]?.goalWeight ?? null;
    const rkGoalBmi = rkGoalW && rkH ? bmi(rkGoalW, rkH) : null;
    const induGoalBmi = induGoalW && induH ? bmi(induGoalW, induH) : null;

    // Age (current)
    const rkAge = calcAge(rkDob);
    const induAge = calcAge(induDob);

    // Body fat % at latest log date
    const rkBf =
      rkBmi && rkLatest
        ? calcBodyFat(rkBmi, rkDob, rkSex, rkLatest.date)
        : null;
    const induBf =
      induBmi && induLatest
        ? calcBodyFat(induBmi, induDob, induSex, induLatest.date)
        : null;

    // Chart data — include historical BF% per entry
    const allDates = [...new Set(logs.map((l) => l.date))].sort();
    const chartData = allDates.map((date) => {
      const rkEntry = rkLogs.find((l) => l.date === date);
      const induEntry = induLogs.find((l) => l.date === date);
      const rkBmiVal = rkEntry && rkH ? bmi(rkEntry.weight, rkH) : null;
      const induBmiVal =
        induEntry && induH ? bmi(induEntry.weight, induH) : null;
      return {
        date,
        rk: rkBmiVal,
        indu: induBmiVal,
        rkBf: rkBmiVal ? calcBodyFat(rkBmiVal, rkDob, rkSex, date) : null,
        induBf: induBmiVal
          ? calcBodyFat(induBmiVal, induDob, induSex, date)
          : null,
      };
    });

    return {
      rkBmi,
      rkPrevBmi,
      rkGoalBmi,
      rkAge,
      rkBf,
      rkSex,
      induBmi,
      induPrevBmi,
      induGoalBmi,
      induAge,
      induBf,
      induSex,
      rkStreak: calcStreak(logs, RK_EMAIL),
      induStreak: calcStreak(logs, INDU_EMAIL),
      chartData,
    };
  }, [logs, profiles]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const saveSetup = async () => {
    const h = parseFloat(setupForm.height);
    if (!h || h < 50 || h > 300) return;
    if (!setupForm.dob || !setupForm.sex) return;
    setSaving(true);
    const payload = { height: h, dob: setupForm.dob, sex: setupForm.sex };
    if (setupForm.goalWeight)
      payload.goalWeight = parseFloat(setupForm.goalWeight);
    await setDoc(
      doc(db, "health", "profiles"),
      { [user.email]: payload },
      { merge: true },
    );
    setSaving(false);
    setShowSetup(false);
  };

  const saveWeight = async () => {
    const w = parseFloat(weightForm.weight);
    if (!w || w < 20 || w > 500 || !weightForm.date) return;
    setSaving(true);
    const existing = logs.find(
      (l) => l.loggedBy === user.email && l.date === weightForm.date,
    );
    if (existing) {
      await updateDoc(doc(db, "healthLogs", existing.id), { weight: w });
    } else {
      await addDoc(collection(db, "healthLogs"), {
        loggedBy: user.email,
        weight: w,
        date: weightForm.date,
        createdAt: serverTimestamp(),
      });
    }
    setSaving(false);
    setShowAddWeight(false);
    setWeightForm({ weight: "", date: todayStr() });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, "healthLogs", deleteTarget.id));
    setDeleteTarget(null);
  };

  const updateGoal = async (email, goalWeight) => {
    const existing = profiles?.[email] || {};
    await setDoc(
      doc(db, "health", "profiles"),
      {
        [email]: {
          ...existing,
          goalWeight: goalWeight ? parseFloat(goalWeight) : null,
        },
      },
      { merge: true },
    );
  };

  if (loadingProfiles) {
    return (
      <div className="bmi-tracker">
        <div className="bmi-loading">Loading health data…</div>
      </div>
    );
  }

  return (
    <div className="bmi-tracker">
      {/* Setup modal */}
      {showSetup && (
        <SetupModal
          form={setupForm}
          setForm={setSetupForm}
          onSave={saveSetup}
          saving={saving}
        />
      )}

      {/* Add weight modal */}
      {showAddWeight && (
        <AddWeightModal
          form={weightForm}
          setForm={setWeightForm}
          onSave={saveWeight}
          onClose={() => setShowAddWeight(false)}
          saving={saving}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="bmi-overlay">
          <div className="bmi-modal bmi-modal--sm">
            <p className="bmi-confirm-text">
              Delete this entry?
              <br />
              <span className="bmi-confirm-sub">
                {fmtDate(deleteTarget.date)} — {deleteTarget.weight} kg
              </span>
            </p>
            <div className="bmi-modal-actions">
              <button
                className="btn-bmi-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button className="btn-bmi-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bmi-header">
        <div>
          <h2 className="bmi-title">⚖️ Our Health Journey</h2>
          <p className="bmi-subtitle">Tracking together, one week at a time</p>
        </div>
        <button
          className="btn-add-weight"
          onClick={() => setShowAddWeight(true)}
        >
          + Log weight
        </button>
      </div>

      {/* Chart */}
      {derived.chartData.length > 0 ? (
        <div className="bmi-chart-card">
          <h3 className="bmi-section-title">BMI Over Time</h3>
          <BmiChart
            chartData={derived.chartData}
            rkGoalBmi={derived.rkGoalBmi}
            induGoalBmi={derived.induGoalBmi}
          />
        </div>
      ) : (
        <div className="bmi-chart-card bmi-chart-empty">
          <span>📈 Log your first weight entry to see the chart</span>
        </div>
      )}

      {/* Stat cards */}
      <div className="bmi-stat-cards">
        <BmiStatCard
          meta={USER_META[RK_EMAIL]}
          bmi={derived.rkBmi}
          prevBmi={derived.rkPrevBmi}
          goalBmi={derived.rkGoalBmi}
          goalWeight={profiles?.[RK_EMAIL]?.goalWeight}
          age={derived.rkAge}
          bodyFat={derived.rkBf}
          sex={derived.rkSex}
          streak={derived.rkStreak}
          isMe={user.email === RK_EMAIL}
          onUpdateGoal={(gw) => updateGoal(RK_EMAIL, gw)}
        />
        <BmiStatCard
          meta={USER_META[INDU_EMAIL]}
          bmi={derived.induBmi}
          prevBmi={derived.induPrevBmi}
          goalBmi={derived.induGoalBmi}
          goalWeight={profiles?.[INDU_EMAIL]?.goalWeight}
          age={derived.induAge}
          bodyFat={derived.induBf}
          sex={derived.induSex}
          streak={derived.induStreak}
          isMe={user.email === INDU_EMAIL}
          onUpdateGoal={(gw) => updateGoal(INDU_EMAIL, gw)}
        />
      </div>

      {/* Log history */}
      {logs.length > 0 && (
        <WeightLogHistory
          logs={logs}
          profiles={profiles}
          userEmail={user.email}
          onDelete={setDeleteTarget}
        />
      )}
    </div>
  );
}

// ── SetupModal ────────────────────────────────────────────────────────────────

function SetupModal({ form, setForm, onSave, saving }) {
  const canSave = form.height && form.dob && form.sex;
  return (
    <div className="bmi-overlay">
      <div className="bmi-modal">
        <div className="bmi-modal-header">
          <h3>Welcome! Let's set up your profile ⚖️</h3>
          <p>
            We need a few details to calculate your BMI and body fat %. You only
            do this once.
          </p>
        </div>
        <div className="bmi-modal-body">
          <label className="bmi-label">
            Your height (cm) *
            <input
              className="bmi-input"
              type="number"
              placeholder="e.g. 170"
              min={50}
              max={300}
              value={form.height}
              onChange={(e) =>
                setForm((f) => ({ ...f, height: e.target.value }))
              }
            />
          </label>
          <label className="bmi-label">
            Date of birth *
            <input
              className="bmi-input"
              type="date"
              value={form.dob}
              onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))}
            />
          </label>
          <div className="bmi-label">
            Biological sex *
            <div className="bmi-sex-toggle">
              {["M", "F"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`bmi-sex-btn ${form.sex === s ? "active" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, sex: s }))}
                >
                  {s === "M" ? "♂ Male" : "♀ Female"}
                </button>
              ))}
            </div>
          </div>
          <label className="bmi-label">
            Goal weight (kg) — optional
            <input
              className="bmi-input"
              type="number"
              placeholder="e.g. 65"
              min={20}
              max={500}
              value={form.goalWeight}
              onChange={(e) =>
                setForm((f) => ({ ...f, goalWeight: e.target.value }))
              }
            />
          </label>
        </div>
        <div className="bmi-modal-actions">
          <button
            className="btn-bmi-save"
            onClick={onSave}
            disabled={saving || !canSave}
          >
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AddWeightModal ────────────────────────────────────────────────────────────

function AddWeightModal({ form, setForm, onSave, onClose, saving }) {
  return (
    <div
      className="bmi-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bmi-modal">
        <div className="bmi-modal-header">
          <h3>Log your weight ⚖️</h3>
          <button className="bmi-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="bmi-modal-body">
          <label className="bmi-label">
            Weight (kg) *
            <input
              className="bmi-input"
              type="number"
              placeholder="e.g. 72.5"
              step="0.1"
              min={20}
              max={500}
              value={form.weight}
              onChange={(e) =>
                setForm((f) => ({ ...f, weight: e.target.value }))
              }
              autoFocus
            />
          </label>
          <label className="bmi-label">
            Date *
            <input
              className="bmi-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
          </label>
        </div>
        <div className="bmi-modal-actions">
          <button className="btn-bmi-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-bmi-save"
            onClick={onSave}
            disabled={saving || !form.weight || !form.date}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── BmiStatCard ───────────────────────────────────────────────────────────────

function BmiStatCard({
  meta,
  bmi,
  prevBmi,
  goalBmi,
  goalWeight,
  age,
  bodyFat,
  sex,
  streak,
  isMe,
  onUpdateGoal,
}) {
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState(goalWeight?.toString() || "");
  const [savingGoal, setSavingGoal] = useState(false);

  const zone = bmi ? getBmiZone(bmi) : null;
  const bfZone = bodyFat && sex ? getBfZone(bodyFat, sex) : null;
  const delta = bmi && prevBmi ? Math.round((bmi - prevBmi) * 10) / 10 : null;

  const trend =
    delta === null
      ? null
      : delta > 0
        ? { arrow: "▲", color: "#e57373", label: `+${delta}` }
        : delta < 0
          ? { arrow: "▼", color: "#66bb6a", label: `${delta}` }
          : { arrow: "→", color: "#8b7b73", label: "±0" };

  const handleGoalSave = async () => {
    setSavingGoal(true);
    await onUpdateGoal(goalInput);
    setSavingGoal(false);
    setEditingGoal(false);
  };

  return (
    <div className="bmi-stat-card" style={{ borderColor: meta.borderColor }}>
      <div className="bmi-card-top">
        <div className="bmi-avatar" style={{ background: meta.lineColor }}>
          {meta.initial}
        </div>
        <div className="bmi-card-name-col">
          <div className="bmi-card-name">{meta.name}</div>
          {age !== null && <div className="bmi-card-age">Age {age}</div>}
        </div>
        {streak > 0 && (
          <div className="bmi-streak-badge">🔥 {streak}w streak</div>
        )}
      </div>

      {bmi ? (
        <>
          <div className="bmi-value-row">
            <span className="bmi-value">{bmi}</span>
            <span className="bmi-value-label">BMI</span>
          </div>
          <div className="bmi-meta-row">
            {zone && (
              <span
                className="bmi-zone-chip"
                style={{ background: zone.chipBg, color: zone.chipText }}
              >
                {zone.label}
              </span>
            )}
            {trend && (
              <span className="bmi-trend" style={{ color: trend.color }}>
                {trend.arrow} {trend.label}
              </span>
            )}
          </div>

          {/* Body fat row */}
          {bodyFat !== null && (
            <div className="bmi-bf-row">
              <span className="bmi-bf-label">Body fat</span>
              <span className="bmi-bf-value">{bodyFat}%</span>
              {bfZone && (
                <span
                  className="bmi-zone-chip bmi-zone-chip--sm"
                  style={{ background: bfZone.chipBg, color: bfZone.chipText }}
                >
                  {bfZone.label}
                </span>
              )}
            </div>
          )}

          {goalBmi && (
            <div className="bmi-goal-line">
              Goal: <strong>{goalBmi}</strong> BMI
            </div>
          )}
        </>
      ) : (
        <div className="bmi-no-data">No entries yet</div>
      )}

      {isMe && (
        <div className="bmi-goal-edit">
          {editingGoal ? (
            <div className="bmi-goal-form">
              <input
                className="bmi-input bmi-input--sm"
                type="number"
                placeholder="Goal kg"
                step="0.5"
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
              />
              <button
                className="btn-bmi-save btn-bmi-save--xs"
                onClick={handleGoalSave}
                disabled={savingGoal}
              >
                {savingGoal ? "…" : "✓"}
              </button>
              <button
                className="btn-bmi-cancel btn-bmi-cancel--xs"
                onClick={() => setEditingGoal(false)}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              className="btn-bmi-edit-goal"
              onClick={() => setEditingGoal(true)}
            >
              {goalWeight ? "Edit goal" : "+ Set goal"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── BmiChart ──────────────────────────────────────────────────────────────────

function BmiChart({ chartData, rkGoalBmi, induGoalBmi }) {
  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={chartData}
        margin={{ top: 10, right: 24, left: -10, bottom: 5 }}
      >
        {/* BMI zone bands */}
        <ReferenceArea y1={0} y2={18.5} fill="#dce8f5" fillOpacity={0.5} />
        <ReferenceArea y1={18.5} y2={25} fill="#d4edda" fillOpacity={0.5} />
        <ReferenceArea y1={25} y2={30} fill="#fff3cd" fillOpacity={0.5} />
        <ReferenceArea y1={30} y2={45} fill="#f8d7da" fillOpacity={0.45} />

        <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,74,66,0.08)" />

        {rkGoalBmi && (
          <ReferenceLine
            y={rkGoalBmi}
            stroke="#f4a5ae"
            strokeDasharray="6 3"
            strokeWidth={1.5}
            label={{
              value: "RK goal",
              fill: "#f4a5ae",
              fontSize: 10,
              position: "insideTopRight",
            }}
          />
        )}
        {induGoalBmi && (
          <ReferenceLine
            y={induGoalBmi}
            stroke="#b9a5f4"
            strokeDasharray="6 3"
            strokeWidth={1.5}
            label={{
              value: "Indu goal",
              fill: "#b9a5f4",
              fontSize: 10,
              position: "insideBottomRight",
            }}
          />
        )}

        <XAxis
          dataKey="date"
          tickFormatter={fmtDateShort}
          tick={{ fontFamily: "Lato", fontSize: 11, fill: "#8b7b73" }}
          tickLine={false}
          axisLine={{ stroke: "rgba(90,74,66,0.15)" }}
        />
        <YAxis
          domain={[14, 38]}
          tickCount={7}
          tick={{ fontFamily: "Lato", fontSize: 11, fill: "#8b7b73" }}
          tickLine={false}
          axisLine={false}
        />

        <Tooltip
          contentStyle={{
            fontFamily: "Lato",
            fontSize: 12,
            borderRadius: 10,
            border: "1px solid #e8c4d6",
            background: "#fffbf7",
            boxShadow: "0 4px 16px rgba(90,74,66,0.1)",
          }}
          formatter={(value, name, props) => {
            if (!value) return ["—", name === "rk" ? "RK" : "Indu"];
            const bf =
              name === "rk" ? props.payload?.rkBf : props.payload?.induBf;
            const label =
              bf != null ? `BMI ${value}  ·  ${bf}% BF` : `BMI ${value}`;
            return [label, name === "rk" ? "RK" : "Indu"];
          }}
          labelFormatter={(d) => fmtDate(d)}
        />

        <Legend
          formatter={(val) => (val === "rk" ? "RK" : "Indu")}
          wrapperStyle={{ fontFamily: "Lato", fontSize: 12, paddingTop: 8 }}
        />

        <Line
          type="monotone"
          dataKey="rk"
          stroke="var(--clr-p, #f4a5ae)"
          strokeWidth={2.5}
          dot={{ r: 4, fill: "var(--clr-p, #f4a5ae)", strokeWidth: 0 }}
          activeDot={{ r: 6, fill: "var(--clr-p, #f4a5ae)" }}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="indu"
          stroke="#b9a5f4"
          strokeWidth={2.5}
          dot={{ r: 4, fill: "#b9a5f4", strokeWidth: 0 }}
          activeDot={{ r: 6, fill: "#b9a5f4" }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── WeightLogHistory ──────────────────────────────────────────────────────────

function WeightLogHistory({ logs, profiles, userEmail, onDelete }) {
  const sorted = [...logs].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="bmi-log-section">
      <h3 className="bmi-section-title">Log History</h3>
      <div className="bmi-log-scroll">
        <div className="bmi-log-table">
          <div className="bmi-log-header">
            <span>Date</span>
            <span>Person</span>
            <span>Weight</span>
            <span>BMI</span>
            <span>Body fat</span>
            <span />
          </div>
          {sorted.map((log) => {
            const meta = USER_META[log.loggedBy];
            const prof = profiles?.[log.loggedBy];
            const h = prof?.height;
            const bmi = h ? calcBmi(log.weight, h) : null;
            const zone = bmi ? getBmiZone(bmi) : null;
            const bf = bmi
              ? calcBodyFat(bmi, prof?.dob, prof?.sex, log.date)
              : null;
            const bfZone = bf && prof?.sex ? getBfZone(bf, prof.sex) : null;
            const canDel = log.loggedBy === userEmail;
            return (
              <div key={log.id} className="bmi-log-row">
                <span className="bmi-log-date">{fmtDateShort(log.date)}</span>
                <span
                  className="bmi-person-chip"
                  style={{ background: meta?.chipBg, color: meta?.lineColor }}
                >
                  {meta?.name ?? log.loggedBy}
                </span>
                <span className="bmi-log-weight">{log.weight} kg</span>
                <span>
                  {bmi ? (
                    <span
                      className="bmi-zone-chip bmi-zone-chip--sm"
                      style={{
                        background: zone?.chipBg,
                        color: zone?.chipText,
                      }}
                    >
                      {bmi}
                    </span>
                  ) : (
                    "—"
                  )}
                </span>
                <span>
                  {bf != null ? (
                    <span
                      className="bmi-zone-chip bmi-zone-chip--sm"
                      style={{
                        background: bfZone?.chipBg,
                        color: bfZone?.chipText,
                      }}
                    >
                      {bf}%
                    </span>
                  ) : (
                    "—"
                  )}
                </span>
                <span>
                  {canDel && (
                    <button
                      className="btn-log-delete"
                      onClick={() => onDelete(log)}
                    >
                      ×
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
